import * as React from "react";
import {
  deserializeColumnFilters,
  isColumnFilterActive,
  serializeColumnFilters,
  type ColumnFilterValue,
  type ColumnFilters,
} from "@/modules/snapshots/grid/sortAndFilter";
import { safeRemoveLocalStorageItem, safeSetLocalStorageItem } from "@/modules/snapshots/grid/localStorage";

/**
 * Column filters persisted per collection. `onFiltersChange` runs synchronously before each change so the
 * caller can reset paging in the same render batch as the new filters.
 */
export const useSnapshotGridColumnFilters = (collectionId: string | undefined, onFiltersChange: () => void) => {
  const columnFiltersKey = `snapshot-column-filters-${collectionId}`;
  const [columnFilters, setColumnFilters] = React.useState<ColumnFilters>(() => {
    try {
      const stored = localStorage.getItem(columnFiltersKey);
      return stored ? deserializeColumnFilters(JSON.parse(stored)) : {};
    } catch {
      return {};
    }
  });

  const handleColumnFilterChange = React.useCallback(
    (columnName: string, value: ColumnFilterValue | undefined) => {
      onFiltersChange();
      setColumnFilters(prev => {
        const next = { ...prev };
        if (value && isColumnFilterActive(value)) {
          next[columnName] = value;
        } else {
          delete next[columnName];
        }
        safeSetLocalStorageItem(columnFiltersKey, JSON.stringify(serializeColumnFilters(next)));
        return next;
      });
    },
    [columnFiltersKey, onFiltersChange],
  );

  const handleClearAllColumnFilters = React.useCallback(() => {
    onFiltersChange();
    setColumnFilters({});
    safeRemoveLocalStorageItem(columnFiltersKey);
  }, [columnFiltersKey, onFiltersChange]);

  return { columnFilters, setColumnFilters, handleColumnFilterChange, handleClearAllColumnFilters };
};
